import {
  createEffect,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
  type Accessor,
  type JSX,
} from 'solid-js'
import { listenForSwipe, type SwipeDirection } from '~/lib/swipe'
import type { RemoteArtworkStatus } from '~/lib/remote-image'
import { RemoteArtwork } from './remote-artwork'

// The detail dialogs' image pager (GLOSSARY.md "Detail slide"), shared by the
// land-skin detail and the starter-pack detail: one remote image showing at
// a time, a dot per slide beneath when there is more than one, one slide per
// discrete horizontal swipe with no wrap, and every open landing on the first
// slide. Every slide's image loads on open behind its own spinner and is left
// blank on failure (ADR 0013); a slide with no image is blank from the start.
//
// The state lives with the dialog, not in its content: the dialog's content
// unmounts on close, and a reopen must keep the images that loaded.
export interface DetailSlidePagerState {
  // Whether the first slide's image has settled — loaded or failed. Every
  // open starts there, so it is what a detail's presentation waits on.
  firstSlideSettled: () => boolean
  index: Accessor<number>
  // undefined = a slide with no usable image, blank from the start.
  imageUrls: () => (string | undefined)[]
  setStatus: (url: string, status: RemoteArtworkStatus) => void
  statusOf: (url: string | undefined) => RemoteArtworkStatus
  step: (direction: SwipeDirection) => void
  swiped: Accessor<boolean>
}

export function createDetailSlidePager(options: {
  imageUrls: () => (string | undefined)[]
  open: () => boolean
  // A change starts the pager over like a reopen: the shell keeps a detail
  // mounted across products so its close animation plays with content.
  resetKey: () => unknown
}): DetailSlidePagerState {
  const [index, setIndex] = createSignal(0)
  // The strip animates only between swipes: a reset to the first slide on
  // open or product change lands instantly rather than sliding back across
  // the reopened dialog.
  const [swiped, setSwiped] = createSignal(false)

  // Load state is keyed by image URL, so a new product's slides start over
  // at the spinner rather than inherit the previous product's outcomes.
  const [loadStates, setLoadStates] = createSignal<
    Record<string, RemoteArtworkStatus>
  >({})
  const statusOf = (url: string | undefined): RemoteArtworkStatus =>
    url === undefined ? 'failed' : (loadStates()[url] ?? 'loading')
  const setStatus = (url: string, status: RemoteArtworkStatus) =>
    setLoadStates((current) => ({ ...current, [url]: status }))

  // Every open starts on the first slide and forgets failures: a failure is
  // not the product's permanent state, so the remounted images try again
  // from the spinner. Loaded outcomes are kept — the cached image paints at
  // once.
  createEffect(
    on(
      () => [options.open(), options.resetKey()] as const,
      ([open]) => {
        if (!open) return
        setIndex(0)
        setSwiped(false)
        setLoadStates((current) =>
          Object.fromEntries(
            Object.entries(current).filter(([, status]) => status !== 'failed'),
          ),
        )
      },
      { defer: true },
    ),
  )

  return {
    firstSlideSettled: () => statusOf(options.imageUrls()[0]) !== 'loading',
    imageUrls: options.imageUrls,
    index,
    setStatus,
    statusOf,
    // One slide per swipe, clamped: the ends do not wrap.
    step: (direction) => {
      const last = options.imageUrls().length - 1
      setSwiped(true)
      setIndex((current) =>
        Math.min(last, Math.max(0, current + (direction === 'left' ? 1 : -1))),
      )
    },
    swiped,
  }
}

// The pager's view. The swipe area also holds the children — the copy that
// follows the showing slide — so everything above the dialog's action row is
// one gesture target.
export function DetailSlidePager(props: {
  children?: JSX.Element
  // Where the slide dots sit relative to the children: the land-skin detail
  // keeps them right under the slide, the starter-pack detail puts them
  // under its caption.
  dotsPosition: 'above-copy' | 'below-copy'
  // The first slide's element, for a presentation that waits on it.
  firstSlideRef?: (slide: HTMLElement) => void
  onImageFailure: (index: number, url: string) => void
  pager: DetailSlidePagerState
  testIdPrefix: string
}) {
  const reportImageFailure = (slideIndex: number, url: string) => {
    props.pager.setStatus(url, 'failed')
    props.onImageFailure(slideIndex, url)
  }

  return (
    // pan-y leaves vertical scrolling to the page.
    <div
      ref={(element) => onCleanup(listenForSwipe(element, props.pager.step))}
      class="flex w-full touch-pan-y flex-col items-center"
    >
      <div
        data-testid={`${props.testIdPrefix}-pager`}
        data-index={props.pager.index()}
        class="w-full overflow-hidden"
      >
        {/* The strip slides sideways to the showing slide after a swipe;
            nothing tracks the finger in between (GLOSSARY.md "Detail
            slide"). */}
        <ul
          class="flex"
          classList={{
            'transition-transform duration-300 ease-out motion-reduce:transition-none':
              props.pager.swiped(),
          }}
          style={{ transform: `translateX(-${props.pager.index() * 100}%)` }}
        >
          <For each={props.pager.imageUrls()}>
            {(url, slideIndex) => (
              <li
                ref={(element) => {
                  if (slideIndex() === 0) props.firstSlideRef?.(element)
                }}
                class="w-full shrink-0"
              >
                <RemoteArtwork
                  class="h-[168px] w-full"
                  onError={() => {
                    if (url !== undefined) {
                      reportImageFailure(slideIndex(), url)
                    }
                  }}
                  onLoad={() => {
                    if (url !== undefined) props.pager.setStatus(url, 'loaded')
                  }}
                  src={url ?? ''}
                  status={props.pager.statusOf(url)}
                  testId={`${props.testIdPrefix}-slide`}
                />
              </li>
            )}
          </For>
        </ul>
      </div>
      <Show when={props.dotsPosition === 'above-copy'}>
        <SlideDots
          class="mt-3"
          pager={props.pager}
          testIdPrefix={props.testIdPrefix}
        />
      </Show>
      {props.children}
      <Show when={props.dotsPosition === 'below-copy'}>
        <SlideDots
          class="mt-6"
          pager={props.pager}
          testIdPrefix={props.testIdPrefix}
        />
      </Show>
    </div>
  )
}

// Indicators only, not controls: the swipe is the one way through the
// slides. A lone slide shows none.
function SlideDots(props: {
  class: string
  pager: DetailSlidePagerState
  testIdPrefix: string
}) {
  return (
    <Show when={props.pager.imageUrls().length > 1}>
      <ol
        data-testid={`${props.testIdPrefix}-dots`}
        aria-hidden="true"
        class={`flex gap-1.5 ${props.class}`}
      >
        <For each={props.pager.imageUrls()}>
          {(_, dotIndex) => (
            <li
              data-testid={`${props.testIdPrefix}-dot`}
              data-active={
                dotIndex() === props.pager.index() ? 'true' : 'false'
              }
              class="size-1.5 rounded-full bg-ink/30 transition-colors data-[active=true]:bg-ink"
            />
          )}
        </For>
      </ol>
    </Show>
  )
}
